import { CrudStoreItemPartner360 } from 'legacy/lut/classes/models/partner360/CrudStoreItemPartner360'
import type { IFormJBPHPContactChangeRequest } from 'legacy/lut/classes/models/partner360/IFormHPContacts'
import type { IFormPBMManagers } from 'legacy/lut/classes/models/partner360/IFormPBMManagers'

import { Logic, Operator } from 'core-components/types'
import { guid } from 'core/classes/helpers/GuidHelper'
import { CrudStore } from 'core/types/crudStore'

import {
  buildPbmReplacementRequests,
  getPendingPbmReplacements,
  type IPbmReplacementPerson,
  type IPendingPbmReplacement,
  isSameEmail,
} from '../lib'

const CHANGE_REQUEST_FORM = { formId: '1217', code: 'jbp-hp-contact-change-request-1217' }
const CRUD_KEY = 'cockpit-pbm-replacement'
const PAGE = { page: 0, pageSize: 1000 }

export class CockpitPbmReplacementError extends Error {
  constructor(public reason: 'notAssigned' | 'alreadyAssigned') {
    super(reason)
  }
}

const teamPbmEmails = new Map<string, Promise<string[]>>()

/** The PBMs who report to the manager, by email, once per manager and session. */
export const fetchCockpitTeamPbmEmails = (managerEmail: string): Promise<string[]> => {
  const key = managerEmail.toLowerCase()
  const cached = teamPbmEmails.get(key)

  if (cached) {
    return cached
  }

  const crud = CrudStore.get<IFormPBMManagers>(CrudStoreItemPartner360.PBMManagers, CRUD_KEY)
  crud.setPaging(PAGE)
  crud.setFilter({
    logic: Logic.AND,
    filters: [{ field: 'ManagerEmail', operator: Operator.EQUAL, value: managerEmail }],
  })

  const request = crud
    .fetch({ force: true })
    .then(({ items }) => [...new Set(items.map(({ PBMEmail }) => PBMEmail).filter(Boolean))])

  request.catch(() => teamPbmEmails.delete(key))
  teamPbmEmails.set(key, request)

  return request
}

/** A partner's HP contacts with the change requests still open for them. */
const fetchPartnerHpContacts = async (partnerId: string) => {
  /* A clone each: the partners of a level are read at once, each under its own filter. */
  const crud = CrudStore.get<IFormJBPHPContactChangeRequest>(
    CrudStoreItemPartner360.JBPHPContactChangeRequest,
    CRUD_KEY
  ).clone()
  crud.setPaging(PAGE)
  crud.setFilter({
    logic: Logic.AND,
    filters: [{ field: 'PartnerId', operator: Operator.EQUAL, value: partnerId }],
  })

  const { items } = await crud.fetch({ force: true })

  return items
}

export const fetchCockpitPendingPbmReplacements = async (
  partnerId: string
): Promise<IPendingPbmReplacement[]> =>
  getPendingPbmReplacements(partnerId, await fetchPartnerHpContacts(partnerId))

interface IRequestCockpitPbmReplacementParams {
  partnerId: string
  fromEmail: string
  to: IPbmReplacementPerson
  manager: IPbmReplacementPerson
}

/**
 * Asks for one PBM of a partner to be replaced by another: a removal and an addition on the HP
 * contacts change request form, which apply once approved.
 */
export const requestCockpitPbmReplacement = async ({
  partnerId,
  fromEmail,
  to,
  manager,
}: IRequestCockpitPbmReplacementParams): Promise<void> => {
  const contacts = await fetchPartnerHpContacts(partnerId)
  const current = contacts.find(
    ({ Email, TicketId }) => !(TicketId > 0) && isSameEmail(Email, fromEmail)
  )

  if (!current) {
    throw new CockpitPbmReplacementError('notAssigned')
  }

  if (contacts.some(({ Email }) => isSameEmail(Email, to.email))) {
    throw new CockpitPbmReplacementError('alreadyAssigned')
  }

  const requests = buildPbmReplacementRequests(current, to, manager).map((request) => ({
    ...request,
    EntryId: -1,
    Status: request.TicketStatus,
    Action: request.RequestAction,
    SignId: `new-${guid()}`,
  }))

  const crud = CrudStore.get<IFormJBPHPContactChangeRequest>(
    CrudStoreItemPartner360.JBPHPContactChangeRequest,
    CRUD_KEY
  )

  await crud.create(CHANGE_REQUEST_FORM, requests)
}
